import { ref, push, set } from 'firebase/database';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { validateVideoForm } from '../utils/uploadValidation';

export function VideoUploadForm({
  isOpen,
  onClose,
  videoForm,
  setVideoForm,
  isUploading,
  canUpload,
  db,
  currentUser,
  onUploadSuccess,
  showToast
}) {
  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateVideoForm(videoForm, showToast)) return;
    if (!canUpload()) {
      showToast('You have reached your video upload limit', 'error');
      return;
    }

    try {
      const videoRef = push(ref(db, `artistContent/videos`));
      await set(videoRef, {
        artistId: currentUser.uid,
        artistName: currentUser.displayName || 'Unknown Artist',
        ...videoForm,
        status: 'pending',
        uploadedAt: new Date().toISOString(),
        approvedAt: null
      });

      showToast('Video uploaded! Awaiting admin approval.', 'success');
      onUploadSuccess();
    } catch (err) {
      console.error('Error uploading video:', err);
      showToast('Failed to upload video', 'error');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Upload Video</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Video Title *</label>
            <input
              type="text"
              value={videoForm.name}
              onChange={(e) => setVideoForm({ ...videoForm, name: e.target.value })}
              placeholder="Enter video title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">YouTube URL *</label>
            <input
              type="url"
              value={videoForm.youtubeUrl}
              onChange={(e) => setVideoForm({ ...videoForm, youtubeUrl: e.target.value })}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Description</label>
            <textarea
              value={videoForm.description}
              onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
              placeholder="Add a description (optional)"
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-xs text-blue-700">
            Your video will be reviewed by our admin team before appearing publicly.
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-900 font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}